import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

import { Direction } from "../../utils/types/common";
import "./i18n";
import { Language, SetCurrentLanguage } from "./i18n";
import { getStorage, setStorage } from "../storage/storageHelper";
import { db } from "@modules/local/db";
import { changeLanguage } from "./changeLanguage";

interface LanguageContextProps {
  language: Language;
  direction: Direction;
  toggleLanguage: (newLanguage: Language) => void;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(
  undefined
);

export const useLanguage = (): LanguageContextProps => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguage] = useState<Language>();
  const direction = useMemo(
    () => (language === "ar" ? "rtl" : "ltr"),
    [language]
  );

  const toggleLanguage = useCallback(async (language: Language) => {
    setLanguage(language);

    changeLanguage(language);
  }, []);

  useEffect(() => {
    (async () => {
      const languageData = await getStorage("LANGUAGE");
      const localLanguage = languageData?.language ?? "ar";

      document.documentElement.setAttribute(
        "dir",
        localLanguage === "ar" ? "rtl" : "ltr"
      );
      document.documentElement.setAttribute("lang", localLanguage);

      setLanguage(localLanguage);
      SetCurrentLanguage(localLanguage);

      if (!languageData)
        setStorage({
          key: "LANGUAGE",
          data: {
            language: localLanguage,
          },
        });
    })();
  }, []);

  // useEffect(() => {
  //   if (language === "ar") {
  //     setDirection("rtl");

  //     document.documentElement.setAttribute("dir", "rtl");
  //     document.documentElement.setAttribute("lang", "ar");
  //   } else {
  //     setDirection("ltr");

  //     document.documentElement.setAttribute("dir", "ltr");
  //     document.documentElement.setAttribute("lang", "en");
  //   }
  // }, [language]);

  if (!language) {
    return null;
  }
  return (
    <LanguageContext.Provider value={{ language, direction, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
